// Top-level shell. Routes match the audit's view-state machine 1:1.
//   /                  → FirmCommandCenter
//   /practice/:area    → PracticeAreaView
//   /matter/:eID       → MatterView
//   /client/:clientId  → ClientView
//   /tag/:tagId        → TagView
// The chrome (NavRail + TopBar) is shared; modals (search, integrations) live
// at the shell so cmd-K works on every page.
//
// Keybindings (registered in the cmd-K effect below):
//   Cmd/Ctrl-K - toggle the command palette
//   Esc        - close the palette, else the drawer
//   g + t      - go to /team
//   g + c      - go to /clients
//   g + m      - go to /cases
//
// Auth screens (`/login`, `/auth/google/return`) intentionally render
// OUTSIDE the chrome — there's no useful nav on them and the user has
// no JWT yet to drive the dashboard.

import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { NavRail } from "./components/chrome/NavRail";
import { SHORTCUT } from "./components/chrome/SearchTrigger";
import { TopBar } from "./components/chrome/TopBar";
import { TrailProvider } from "./components/chrome/trail";
import { CommandPalette } from "./components/search/CommandPalette";
import IntegrationsIndex from "./pages/index/IntegrationsIndex";
import { useIsMobile } from "./hooks/useIsMobile";
import { FirmCommandCenter } from "./components/firm/FirmCommandCenter";
import { PracticeAreaView } from "./components/firm/PracticeAreaView";
import { TagView } from "./components/firm/TagView";
import { MatterView } from "./components/matter/MatterView";
import { ClientView } from "./components/client/ClientView";
import { TeamCommandCenter } from "./components/team/TeamCommandCenter";
import { MemberDetail } from "./components/team/MemberDetail";
import { JudgeDetail } from "./components/judge/JudgeDetail";
import { OpposingCounselDetail } from "./components/opposing/OpposingCounselDetail";
import Login from "./pages/Login";
import SetPassword from "./pages/SetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import AuthGoogleReturn from "./pages/AuthGoogleReturn";
import { Profile } from "./pages/Profile";
import { FirmSettings } from "./pages/FirmSettings";
import { EmailLog } from "./pages/settings/EmailLog";
import { FormsSettings } from "./pages/settings/FormsSettings";
import { ModulesSettings } from "./pages/settings/ModulesSettings";
import { CasesIndex } from "./pages/index/CasesIndex";
import { ClientsIndex } from "./pages/index/ClientsIndex";
import { CalendarIndex } from "./pages/index/CalendarIndex";
import { FilingsIndex } from "./pages/index/FilingsIndex";
import { InboxIndex } from "./pages/index/InboxIndex";
import { RequireAuth } from "./components/auth/RequireAuth";
import { RequireClientAuth } from "./components/portal/RequireClientAuth";
import { ScreensContext, type ModuleRoute } from "./components/chrome/screens";
import { MODULE_SCREENS } from "./screens";
import PortalLogin from "./pages/portal/PortalLogin";
import PortalSetPassword from "./pages/portal/PortalSetPassword";
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalDocuments from "./pages/portal/PortalDocuments";

function moduleRoutes(wrapper: ModuleRoute["wrapper"]) {
  return MODULE_SCREENS.flatMap((s) => s.routes.filter((r) => r.wrapper === wrapper));
}

export default function App() {
  return (
    <ScreensContext.Provider value={MODULE_SCREENS}>
      <Routes>
        {/* Auth screens - no chrome. */}
        <Route path="/login" element={<Login />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/google/return" element={<AuthGoogleReturn />} />

        {/* A module's public doors (the intake questionnaire a client opens
          from the office's link, or from the mailed code): their own
          credential, no firm chrome, outside RequireAuth like the portal. */}
        {moduleRoutes("public").map((r) => (
          <Route key={r.path} path={r.path} element={r.element} />
        ))}

        {/* Client portal. Its own space, its own credential, no firm chrome -
          /client/:clientId is already a staff screen, hence /portal. */}
        <Route path="/portal/login" element={<PortalLogin />} />
        {/* The emailed link carries its own credential, so this one sits
          outside RequireClientAuth like the sign-in screen. */}
        <Route path="/portal/set-password" element={<PortalSetPassword />} />
        <Route
          path="/portal"
          element={
            <RequireClientAuth>
              <PortalDashboard />
            </RequireClientAuth>
          }
        />
        <Route
          path="/portal/documents"
          element={
            <RequireClientAuth>
              <PortalDocuments />
            </RequireClientAuth>
          }
        />
        {moduleRoutes("portal").map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={<RequireClientAuth>{r.element}</RequireClientAuth>}
          />
        ))}
        {/* Any other /portal path belongs to the client, never to the staff
          login the catch-all below would send them to. */}
        <Route path="/portal/*" element={<Navigate to="/portal" replace />} />

        {/* Everything else renders inside the chrome shell, gated by
          RequireAuth so an unauthenticated visitor is redirected to
          /login with their requested URL preserved in ?return_to=. */}
        <Route
          path="*"
          element={
            <RequireAuth>
              <ChromeShell />
            </RequireAuth>
          }
        />
      </Routes>
    </ScreensContext.Provider>
  );
}

function ChromeShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const isMobile = useIsMobile();
  useEffect(() => {
    if (!isMobile && drawerOpen) setDrawerOpen(false);
  }, [isMobile, drawerOpen]);

  // Cmd-K + Esc + g-prefix chord keybindings.
  //
  // The g-chord follows the GitHub / Linear convention: press `g`, then
  // within 1.2s press one of `t` (team), `c` (clients), `m` (matters)
  // to navigate. We disable the chord when focus is in an editable element
  // so typing into a text field never accidentally jumps the route.
  const navigate = useNavigate();
  const gPrefixUntilRef = useRef<number>(0);
  useEffect(() => {
    const isEditable = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null;
      if (!node) return false;
      const tag = node.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (node.isContentEditable) return true;
      return false;
    };

    const onKey = (e: KeyboardEvent) => {
      // Cmd/Ctrl-K: open palette. This works even inside text fields, and
      // by key position, so a Cyrillic layout or Caps Lock still lands it;
      // by letter too, for a layout that puts k elsewhere. Not with Alt or
      // Shift: AltGr is Ctrl+Alt on Windows, Ctrl+Shift+K is the browser's.
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.altKey &&
        !e.shiftKey &&
        (e.code === "KeyK" || e.key.toLowerCase() === "k")
      ) {
        e.preventDefault();
        setSearchOpen((o) => !o);
        gPrefixUntilRef.current = 0;
        return;
      }
      // Escape: close any open overlay.
      if (e.key === "Escape") {
        if (searchOpen) setSearchOpen(false);
        else if (drawerOpen) setDrawerOpen(false);
        gPrefixUntilRef.current = 0;
        return;
      }

      // g-chord shortcuts. Skip when typing into a text field, when an
      // overlay is open, or when modifier keys are pressed.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (searchOpen || drawerOpen) return;
      if (isEditable(e.target)) return;

      const now = Date.now();
      if (e.key === "g") {
        e.preventDefault();
        gPrefixUntilRef.current = now + 1200;
        return;
      }

      if (gPrefixUntilRef.current > now) {
        if (e.key === "t") {
          e.preventDefault();
          gPrefixUntilRef.current = 0;
          navigate("/team");
          return;
        }
        if (e.key === "c") {
          e.preventDefault();
          gPrefixUntilRef.current = 0;
          // /clients lists every client; the palette is one cmd-K away
          // if the user wants free-text search instead of scrolling.
          navigate("/clients");
          return;
        }
        if (e.key === "m") {
          e.preventDefault();
          gPrefixUntilRef.current = 0;
          // /cases is the canonical matter index; cmd-K still works for
          // jump-to-eID search.
          navigate("/cases");
          return;
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen, drawerOpen, navigate]);

  // Close drawer on route navigation so picking a destination from the rail
  // doesn't leave the overlay sitting on top of the new page.
  const location = useLocation();
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="cc-app" data-drawer={drawerOpen ? "open" : "closed"}>
      <NavRail onOpenSearch={openSearch} />

      <button
        type="button"
        className="cc-drawer-scrim"
        aria-label="Close menu"
        onClick={closeDrawer}
        tabIndex={drawerOpen ? 0 : -1}
      />

      <TrailProvider>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <TopBar onOpenSearch={openSearch} onOpenDrawer={openDrawer} />

          <Routes>
            <Route path="/" element={<FirmCommandCenter onOpenSearch={openSearch} />} />
            <Route path="/practice/:area" element={<PracticeAreaView />} />
            <Route path="/matter/:eID" element={<MatterView />} />
            <Route path="/client/:clientId" element={<ClientView />} />
            <Route path="/judge/:judgeId" element={<JudgeDetail />} />
            <Route path="/opposing/:counselId" element={<OpposingCounselDetail />} />
            <Route path="/tag/:tagId" element={<TagView />} />
            <Route path="/team" element={<TeamCommandCenter />} />
            <Route path="/team/:userId" element={<MemberDetail />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings/firm" element={<FirmSettings />} />
            <Route path="/settings/modules" element={<ModulesSettings />} />
            <Route path="/cases" element={<CasesIndex />} />
            <Route path="/clients" element={<ClientsIndex />} />
            <Route path="/calendar" element={<CalendarIndex />} />
            <Route path="/filings" element={<FilingsIndex />} />
            <Route path="/inbox" element={<InboxIndex />} />
            <Route path="/settings/integrations" element={<IntegrationsIndex />} />
            <Route path="/settings/email" element={<EmailLog />} />
            <Route path="/settings/forms" element={<FormsSettings />} />
            {moduleRoutes("chrome").map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
            {/* Paths that moved: kept so an old bookmark still lands. */}
            <Route
              path="/integrations"
              element={<Navigate to="/settings/integrations" replace />}
            />
            <Route path="*" element={<NotFound onSearch={openSearch} />} />
          </Routes>
        </div>
      </TrailProvider>

      <CommandPalette open={searchOpen} onClose={closeSearch} />
    </div>
  );
}

function NotFound({ onSearch }: { onSearch: () => void }) {
  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-card" style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Page not found</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>
            Try the Command Center, or press {SHORTCUT} to jump to a client, matter, or screen.
          </div>
          <button className="cc-link-btn" onClick={onSearch} style={{ marginTop: 12 }}>
            Open search
          </button>
        </div>
      </div>
    </main>
  );
}
